import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Plane, LogOut, Users, DollarSign, Calendar, TrendingUp,
  Settings, FileText, MapPin, Menu, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { api } from '../../utils/apiClient';
import { motion } from 'framer-motion';
import FlightsManagementV2 from '../../components/admin/FlightsManagementV2';
import PricingManagement from '../../components/admin/PricingManagement';
import BookingsManagement from '../../components/admin/BookingsManagement';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [stats, setStats] = useState({
    totalFlights: 0,
    todayBookings: 0,
    revenue: 0,
    occupancy: 0
  });

  useEffect(() => {
    // Verificar autenticación
    const token = localStorage.getItem('auth_token');
    const user = localStorage.getItem('admin_username');
    
    if (!token) {
      navigate('/admin/login');
      return;
    }

    setUsername(user || 'Admin');
    
    // Cargar estadísticas
    loadStats();
  }, [navigate]);

  const loadStats = async () => {
    try {
      // Cargar vuelos
      const flightsResponse = await api.get('/api/admin/flights/all');
      
      // Cargar estadísticas de reservas
      const bookingsStats = await api.get('/api/admin/bookings/stats/summary');
      
      setStats({
        totalFlights: flightsResponse.total || 0,
        todayBookings: bookingsStats.today || 0,
        revenue: bookingsStats.total_revenue || 0,
        occupancy: 85 // TODO: Calcular real basado en asientos vendidos
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('admin_username');
    navigate('/admin/login');
  };

  const menuItems = [
    { id: 'dashboard', icon: TrendingUp, label: 'Dashboard' },
    { id: 'flights', icon: Plane, label: 'Gestión de Vuelos' },
    { id: 'prices', icon: DollarSign, label: 'Gestión de Precios' },
    { id: 'bookings', icon: Calendar, label: 'Reservas' },
    { id: 'content', icon: FileText, label: 'Contenido', badge: 'Pronto' },
  ];

  const statsCards = [
    {
      title: 'Total Vuelos',
      value: stats.totalFlights,
      icon: Plane,
      color: 'from-blue-900 to-blue-700',
      change: '+2 este mes'
    },
    {
      title: 'Reservas Hoy',
      value: stats.todayBookings,
      icon: Calendar,
      color: 'from-green-600 to-green-500',
      change: 'Pendiente'
    },
    {
      title: 'Ingresos del Mes',
      value: `$${stats.revenue.toLocaleString()}`,
      icon: DollarSign,
      color: 'from-amber-500 to-amber-600',
      change: 'MXN'
    },
    {
      title: 'Ocupación',
      value: `${stats.occupancy}%`,
      icon: Users,
      color: 'from-purple-600 to-purple-500',
      change: 'Promedio'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <motion.aside
        initial={{ x: -300 }}
        animate={{ x: sidebarOpen ? 0 : -300 }}
        className={`fixed lg:relative z-30 w-64 bg-gradient-to-b from-blue-900 to-blue-800 text-white h-screen overflow-y-auto`}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-3">
              <Plane className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold">Aerotucán</h1>
                <p className="text-xs text-blue-200">Admin Panel</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* User Info */}
          <div className="mb-6 p-4 bg-white/10 rounded-lg">
            <p className="text-sm text-blue-200">Conectado como:</p>
            <p className="font-semibold">{username}</p>
          </div>

          {/* Menu */}
          <nav className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                    activeSection === item.id
                      ? 'bg-white text-blue-900 shadow-lg'
                      : 'hover:bg-white/10'
                  }`}
                  disabled={item.badge === 'Pronto'}
                >
                  <div className="flex items-center space-x-3">
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </div>
                  {item.badge && (
                    <span className="text-xs px-2 py-1 bg-amber-500 text-white rounded-full">
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="w-full mt-8 flex items-center space-x-3 px-4 py-3 rounded-lg hover:bg-red-600 transition-all"
          >
            <LogOut className="w-5 h-5" />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {/* Top Bar */}
        <header className="bg-white shadow-sm sticky top-0 z-20">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden"
              >
                <Menu className="w-6 h-6" />
              </button>
              <h2 className="text-xl font-bold text-gray-900">
                {menuItems.find(i => i.id === activeSection)?.label || 'Dashboard'}
              </h2>
            </div>
            <Link to="/" className="text-sm text-blue-900 hover:underline">
              Ver sitio público →
            </Link>
          </div>
        </header>

        {/* Content */}
        <main className="p-6">
          {activeSection === 'dashboard' && (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {statsCards.map((stat, index) => {
                  const Icon = stat.icon;
                  return (
                    <motion.div
                      key={stat.title}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Card className="border-0 shadow-lg overflow-hidden">
                        <CardContent className="p-0">
                          <div className={`bg-gradient-to-br ${stat.color} p-4 text-white`}>
                            <Icon className="w-8 h-8 mb-2 opacity-80" />
                            <p className="text-sm opacity-90">{stat.title}</p>
                            <p className="text-3xl font-bold mt-1">{stat.value}</p>
                            <p className="text-xs mt-1 opacity-75">{stat.change}</p>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>

              {/* Quick Actions */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold mb-4">Acciones Rápidas</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Button
                      onClick={() => setActiveSection('flights')}
                      className="bg-blue-900 hover:bg-blue-800"
                    >
                      Gestionar Vuelos
                    </Button>
                    <Button
                      onClick={() => setActiveSection('prices')}
                      className="bg-amber-500 hover:bg-amber-600"
                    >
                      Actualizar Precios
                    </Button>
                    <Button
                      onClick={() => setActiveSection('bookings')}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Ver Reservas
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {activeSection === 'flights' && <FlightsManagementV2 />}
          {activeSection === 'prices' && <PricingManagement />}
          {activeSection === 'bookings' && <BookingsManagement />}
          
          {activeSection === 'content' && (
            <Card>
              <CardContent className="p-6 text-center">
                <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <h3 className="text-xl font-bold mb-2">Gestión de Contenido</h3>
                <p className="text-gray-600">Esta funcionalidad estará disponible próximamente.</p>
              </CardContent>
            </Card>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
